import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GraphQLClient } from '../graphql/client';
import { AppConfigService } from './app-config.service';
import { MembershipService } from './membership.service';

/** At most ONE re-read per window while refusals persist — never a loop, and never a once-per-session latch. */
const REFRESH_WINDOW_MS = 60_000;

/**
 * The switch going OFF under a live visitor. The server refuses every MODULE read of a public brand whose public
 * access was just turned off (the brand portal's reads serve on, by the portal's own published flag), and a visitor's
 * page would show read-failed faces for the rest of the session.
 * This service consumes the GraphQL client's refusal notification — the client's one throw choke point: a
 * page's CAUGHT read error never reaches the global error handler, which is why the trigger is not seated
 * there — and, in public mode, re-reads the tenant config: when the switch is off, public mode ends and the URL
 * is re-run at once (a face route re-matches through its host's own effect; a public page is re-run by the shell
 * inside the rebuild window, so its door's guard runs again — either way the shipped guards answer as on any OFF
 * brand and the visitor meets the brand portal, its branded gate only while the portal itself is unpublished). At
 * most one re-read per window while refusals
 * persist: a first re-read can answer a stale value from the config's response cache, so a once-per-session
 * latch would strand the visitor. Constructed at app boot by the shell's inject, as the paused-redirect
 * service is; the shell's error handler withholds the refusal toast in public mode for the same reason.
 */
@Injectable({ providedIn: 'root' })
export class PublicModeConfigRefreshService {
  private readonly membership = inject(MembershipService);
  private readonly appConfig = inject(AppConfigService);

  /** The issue instant of the last re-read; a refusal inside the window re-reads nothing. */
  private lastReadAt: number | null = null;

  constructor() {
    // Root singletons leak nothing in production, but undestroyed subscriptions bleed across TestBed
    // instances in the spec harness — tie to the injector's lifetime.
    inject(GraphQLClient).authRefused$
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => this.onRefused());
  }

  private onRefused(): void {
    if (!this.membership.publicMode()) return;
    const now = Date.now();
    if (this.lastReadAt !== null && now - this.lastReadAt < REFRESH_WINDOW_MS) return;
    this.lastReadAt = now;
    // The re-read completes without a value on failure (its own contract) — nothing to catch here.
    this.appConfig.refresh().subscribe();
  }
}
